import type { IFinanceRepository } from "../repositories/FinanceRepository";
import type {
  StudyBudget,
  SiteContract,
  Payment,
  FinancialDashboardData,
  FinanceAuditLog,
  CreateBudgetRequest,
  UpdateBudgetRequest,
  CreateBudgetCategoryRequest,
  UpdateBudgetCategoryRequest,
  CreateContractRequest,
  UpdateContractRequest,
  CreateContractVersionRequest,
  SubmitInvoiceRequest,
  UpdateInvoiceRequest,
  ConfirmPaymentRequest,
  FinanceFilters,
  AuditLogFilters,
  FinanceListResponse,
} from "@/pages/Finance/types";
import {
  assertUploadWithin10Mb,
  uploadSizeLimitApiMessage,
} from "@/constants/uploadLimits";
import type {
  BudgetExpenditure,
  CategoryTypeOption,
  CreateExpenditureRequest,
  DocumentDownloadUrlResponse,
  TriggerTypeOption,
  UploadContractDocumentResponse,
  UploadInvoiceDocumentResponse,
  UploadPaymentProofResponse,
} from "@/types/finance.type";
// ===================== Use Case Interface =====================
export interface IFinanceUseCase {
  // Budget
  createBudget(budget: CreateBudgetRequest): Promise<StudyBudget>;
  getBudgets(
    filters?: FinanceFilters,
    page?: number,
    limit?: number,
  ): Promise<FinanceListResponse<StudyBudget>>;
  getBudgetByStudy(studyId: string): Promise<StudyBudget>;
  getBudgetById(id: string): Promise<StudyBudget>;
  updateBudget(id: string, updates: UpdateBudgetRequest): Promise<StudyBudget>;
  addBudgetCategory(
    budgetId: string,
    category: CreateBudgetCategoryRequest,
  ): Promise<StudyBudget>;
  updateBudgetCategory(
    budgetId: string,
    categoryId: string,
    updates: UpdateBudgetCategoryRequest,
  ): Promise<StudyBudget>;
  deleteBudgetCategory(
    budgetId: string,
    categoryId: string,
  ): Promise<StudyBudget>;
  deleteBudget(id: string): Promise<void>;
  getCategoryTypes(): Promise<CategoryTypeOption[]>;
  // Expenditures
  recordExpenditure(
    budgetId: string,
    categoryId: string,
    data: CreateExpenditureRequest,
  ): Promise<BudgetExpenditure>;
  getExpenditures(
    budgetId: string,
    categoryId: string,
  ): Promise<{
    items: BudgetExpenditure[];
    total: number;
    totalSpent: number;
  }>;
  deleteExpenditure(
    budgetId: string,
    categoryId: string,
    expenditureId: string,
  ): Promise<void>;
  // Contract
  createContract(contract: CreateContractRequest): Promise<SiteContract>;
  getContracts(
    filters?: FinanceFilters,
    page?: number,
    limit?: number,
  ): Promise<FinanceListResponse<SiteContract>>;
  getContractByStudySite(
    studyId: string,
    siteId: string,
  ): Promise<SiteContract>;
  getContractById(id: string): Promise<SiteContract>;
  updateContract(
    id: string,
    updates: UpdateContractRequest,
  ): Promise<SiteContract>;
  createContractVersion(
    id: string,
    data?: CreateContractVersionRequest,
  ): Promise<SiteContract>;
  getContractVersions(id: string): Promise<SiteContract[]>;
  uploadContractDocument(file: File): Promise<UploadContractDocumentResponse>;
  getContractDownloadUrl(id: string): Promise<DocumentDownloadUrlResponse>;
  deleteContract(id: string): Promise<void>;
  getTriggerTypes(): Promise<TriggerTypeOption[]>;
  // Payment
  getPayments(
    filters?: FinanceFilters,
    page?: number,
    limit?: number,
  ): Promise<FinanceListResponse<Payment>>;
  getPaymentsByContract(contractId: string): Promise<Payment[]>;
  getPaymentById(id: string): Promise<Payment>;
  markPaymentDue(id: string): Promise<Payment>;
  submitInvoice(id: string, invoice: SubmitInvoiceRequest): Promise<Payment>;
  updateInvoice(id: string, invoice: UpdateInvoiceRequest): Promise<Payment>;
  confirmPayment(id: string, data: ConfirmPaymentRequest): Promise<Payment>;
  lockPayment(id: string): Promise<Payment>;
  deletePayment(id: string): Promise<void>;
  uploadInvoiceDocument(
    id: string,
    file: File,
  ): Promise<UploadInvoiceDocumentResponse>;
  getInvoiceDownloadUrl(id: string): Promise<DocumentDownloadUrlResponse>;
  uploadPaymentProofDocument(
    id: string,
    file: File,
  ): Promise<UploadPaymentProofResponse>;
  getPaymentProofDownloadUrl(id: string): Promise<DocumentDownloadUrlResponse>;
  // Dashboard
  getFinancialDashboard(studyId: string): Promise<FinancialDashboardData>;
  // Audit
  getFinanceAuditLogs(
    filters?: AuditLogFilters,
    page?: number,
    limit?: number,
  ): Promise<FinanceListResponse<FinanceAuditLog>>;
  exportFinanceAuditLogs(studyId: string, format?: string): Promise<Blob>;
}
// ===================== Error Handling Helper =====================
function handleError(error: unknown, defaultMessage: string): never {
  const err = error as {
    status?: number;
    message?: string;
  };
  if (err.status === 401) {
    throw new Error("Authentication failed: Please login again");
  }
  if (err.status === 403) {
    throw new Error("Access denied: Insufficient permissions");
  }
  if (err.status === 404) {
    throw new Error("Resource not found");
  }
  if (err.status === 400) {
    throw new Error(err.message ?? "Invalid data: Please check your input");
  }
  const sizeMessage = uploadSizeLimitApiMessage(error);
  if (sizeMessage) {
    throw new Error(sizeMessage);
  }
  throw new Error(err.message ?? defaultMessage);
}
// ===================== Use Case Implementation =====================
export class FinanceUseCase implements IFinanceUseCase {
  constructor(private financeRepository: IFinanceRepository) {}
  // ---------------------- Budget ----------------------
  async createBudget(budget: CreateBudgetRequest): Promise<StudyBudget> {
    if (!budget.studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!budget.totalBudget || budget.totalBudget <= 0) {
      throw new Error("Total budget must be greater than 0");
    }
    if (!budget.currency?.trim()) {
      throw new Error("Currency is required");
    }
    try {
      return await this.financeRepository.createBudget(budget);
    } catch (error) {
      handleError(error, "Failed to create budget");
    }
  }
  async getBudgets(
    filters?: FinanceFilters,
    page = 1,
    limit = 10,
  ): Promise<FinanceListResponse<StudyBudget>> {
    try {
      return await this.financeRepository.getBudgets(filters, page, limit);
    } catch (error) {
      handleError(error, "Failed to fetch budgets");
    }
  }
  async getBudgetByStudy(studyId: string): Promise<StudyBudget> {
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    try {
      return await this.financeRepository.getBudgetByStudy(studyId);
    } catch (error) {
      handleError(error, "Failed to fetch budget for study");
    }
  }
  async getBudgetById(id: string): Promise<StudyBudget> {
    if (!id?.trim()) {
      throw new Error("Budget ID is required");
    }
    try {
      return await this.financeRepository.getBudgetById(id);
    } catch (error) {
      handleError(error, "Failed to fetch budget");
    }
  }
  async updateBudget(
    id: string,
    updates: UpdateBudgetRequest,
  ): Promise<StudyBudget> {
    if (!id?.trim()) {
      throw new Error("Budget ID is required");
    }
    if (updates.totalBudget !== undefined && updates.totalBudget <= 0) {
      throw new Error("Total budget must be greater than 0");
    }
    try {
      return await this.financeRepository.updateBudget(id, updates);
    } catch (error) {
      handleError(error, "Failed to update budget");
    }
  }
  async addBudgetCategory(
    budgetId: string,
    category: CreateBudgetCategoryRequest,
  ): Promise<StudyBudget> {
    if (!budgetId?.trim()) {
      throw new Error("Budget ID is required");
    }
    if (!category.categoryType?.trim()) {
      throw new Error("Category type is required");
    }
    if (!category.categoryName?.trim()) {
      throw new Error("Category name is required");
    }
    if (!category.allocatedAmount || category.allocatedAmount <= 0) {
      throw new Error("Allocated amount must be greater than 0");
    }
    try {
      return await this.financeRepository.addBudgetCategory(budgetId, category);
    } catch (error) {
      handleError(error, "Failed to add budget category");
    }
  }
  async updateBudgetCategory(
    budgetId: string,
    categoryId: string,
    updates: UpdateBudgetCategoryRequest,
  ): Promise<StudyBudget> {
    if (!budgetId?.trim()) {
      throw new Error("Budget ID is required");
    }
    if (!categoryId?.trim()) {
      throw new Error("Category ID is required");
    }
    try {
      return await this.financeRepository.updateBudgetCategory(
        budgetId,
        categoryId,
        updates,
      );
    } catch (error) {
      handleError(error, "Failed to update budget category");
    }
  }
  async deleteBudgetCategory(
    budgetId: string,
    categoryId: string,
  ): Promise<StudyBudget> {
    if (!budgetId?.trim()) {
      throw new Error("Budget ID is required");
    }
    if (!categoryId?.trim()) {
      throw new Error("Category ID is required");
    }
    try {
      return await this.financeRepository.deleteBudgetCategory(
        budgetId,
        categoryId,
      );
    } catch (error) {
      handleError(error, "Failed to delete budget category");
    }
  }
  async deleteBudget(id: string): Promise<void> {
    if (!id?.trim()) {
      throw new Error("Budget ID is required");
    }
    try {
      await this.financeRepository.deleteBudget(id);
    } catch (error) {
      handleError(error, "Failed to delete budget");
    }
  }
  async getCategoryTypes(): Promise<CategoryTypeOption[]> {
    try {
      return await this.financeRepository.getCategoryTypes();
    } catch (error) {
      handleError(error, "Failed to fetch category types");
      return [];
    }
  }
  // ---------------------- Expenditures ----------------------
  async recordExpenditure(
    budgetId: string,
    categoryId: string,
    data: CreateExpenditureRequest,
  ): Promise<BudgetExpenditure> {
    if (!budgetId?.trim()) {
      throw new Error("Budget ID is required");
    }
    if (!categoryId?.trim()) {
      throw new Error("Category ID is required");
    }
    if (!data.amount || data.amount <= 0) {
      throw new Error("Amount must be greater than 0");
    }
    if (!data.expenditureDate?.trim()) {
      throw new Error("Expenditure date is required");
    }
    try {
      return await this.financeRepository.recordExpenditure(
        budgetId,
        categoryId,
        data,
      );
    } catch (error) {
      handleError(error, "Failed to record expenditure");
    }
  }
  async getExpenditures(
    budgetId: string,
    categoryId: string,
  ): Promise<{
    items: BudgetExpenditure[];
    total: number;
    totalSpent: number;
  }> {
    if (!budgetId?.trim()) {
      throw new Error("Budget ID is required");
    }
    if (!categoryId?.trim()) {
      throw new Error("Category ID is required");
    }
    try {
      return await this.financeRepository.getExpenditures(budgetId, categoryId);
    } catch (error) {
      handleError(error, "Failed to fetch expenditures");
    }
  }
  async deleteExpenditure(
    budgetId: string,
    categoryId: string,
    expenditureId: string,
  ): Promise<void> {
    if (!budgetId?.trim()) {
      throw new Error("Budget ID is required");
    }
    if (!categoryId?.trim()) {
      throw new Error("Category ID is required");
    }
    if (!expenditureId?.trim()) {
      throw new Error("Expenditure ID is required");
    }
    try {
      await this.financeRepository.deleteExpenditure(
        budgetId,
        categoryId,
        expenditureId,
      );
    } catch (error) {
      handleError(error, "Failed to delete expenditure");
    }
  }
  async getTriggerTypes(): Promise<TriggerTypeOption[]> {
    try {
      return await this.financeRepository.getTriggerTypes();
    } catch (error) {
      handleError(error, "Failed to fetch trigger types");
      return [];
    }
  }
  // ---------------------- Contract ----------------------
  async createContract(contract: CreateContractRequest): Promise<SiteContract> {
    if (!contract.studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!contract.siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    if (!contract.contractValue || contract.contractValue <= 0) {
      throw new Error("Contract value must be greater than 0");
    }
    if (!contract.currency?.trim()) {
      throw new Error("Currency is required");
    }
    if (!contract.triggerType?.trim()) {
      throw new Error("Trigger type is required");
    }
    if (!contract.contractDocumentKey?.trim()) {
      throw new Error("Contract document is required");
    }
    if (!contract.contractDocumentName?.trim()) {
      throw new Error("Contract document name is required");
    }
    try {
      return await this.financeRepository.createContract(contract);
    } catch (error) {
      handleError(error, "Failed to create contract");
    }
  }
  async getContracts(
    filters?: FinanceFilters,
    page = 1,
    limit = 10,
  ): Promise<FinanceListResponse<SiteContract>> {
    try {
      return await this.financeRepository.getContracts(filters, page, limit);
    } catch (error) {
      handleError(error, "Failed to fetch contracts");
    }
  }
  async getContractByStudySite(
    studyId: string,
    siteId: string,
  ): Promise<SiteContract> {
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    try {
      return await this.financeRepository.getContractByStudySite(
        studyId,
        siteId,
      );
    } catch (error) {
      handleError(error, "Failed to fetch contract");
    }
  }
  async getContractById(id: string): Promise<SiteContract> {
    if (!id?.trim()) {
      throw new Error("Contract ID is required");
    }
    try {
      return await this.financeRepository.getContractById(id);
    } catch (error) {
      handleError(error, "Failed to fetch contract");
    }
  }
  async updateContract(
    id: string,
    updates: UpdateContractRequest,
  ): Promise<SiteContract> {
    if (!id?.trim()) {
      throw new Error("Contract ID is required");
    }
    try {
      return await this.financeRepository.updateContract(id, updates);
    } catch (error) {
      handleError(error, "Failed to update contract");
    }
  }
  async createContractVersion(
    id: string,
    data?: CreateContractVersionRequest,
  ): Promise<SiteContract> {
    if (!id?.trim()) {
      throw new Error("Contract ID is required");
    }
    try {
      return await this.financeRepository.createContractVersion(id, data);
    } catch (error) {
      handleError(error, "Failed to create contract version");
    }
  }
  async getContractVersions(id: string): Promise<SiteContract[]> {
    if (!id?.trim()) {
      throw new Error("Contract ID is required");
    }
    try {
      return await this.financeRepository.getContractVersions(id);
    } catch (error) {
      handleError(error, "Failed to fetch contract versions");
    }
  }
  async deleteContract(id: string): Promise<void> {
    if (!id?.trim()) {
      throw new Error("Contract ID is required");
    }
    try {
      await this.financeRepository.deleteContract(id);
    } catch (error) {
      handleError(error, "Failed to delete contract");
    }
  }
  async uploadContractDocument(
    file: File,
  ): Promise<UploadContractDocumentResponse> {
    assertUploadWithin10Mb(file);
    try {
      return await this.financeRepository.uploadContractDocument(file);
    } catch (error) {
      handleError(error, "Failed to upload contract document");
    }
  }
  async getContractDownloadUrl(
    id: string,
  ): Promise<DocumentDownloadUrlResponse> {
    if (!id?.trim()) {
      throw new Error("Contract ID is required");
    }
    try {
      return await this.financeRepository.getContractDownloadUrl(id);
    } catch (error) {
      handleError(error, "Failed to get contract download URL");
    }
  }
  // ---------------------- Payment ----------------------
  async getPayments(
    filters?: FinanceFilters,
    page = 1,
    limit = 10,
  ): Promise<FinanceListResponse<Payment>> {
    try {
      return await this.financeRepository.getPayments(filters, page, limit);
    } catch (error) {
      handleError(error, "Failed to fetch payments");
    }
  }
  async getPaymentsByContract(contractId: string): Promise<Payment[]> {
    if (!contractId?.trim()) {
      throw new Error("Contract ID is required");
    }
    try {
      return await this.financeRepository.getPaymentsByContract(contractId);
    } catch (error) {
      handleError(error, "Failed to fetch payments for contract");
    }
  }
  async getPaymentById(id: string): Promise<Payment> {
    if (!id?.trim()) {
      throw new Error("Payment ID is required");
    }
    try {
      return await this.financeRepository.getPaymentById(id);
    } catch (error) {
      handleError(error, "Failed to fetch payment");
    }
  }
  async markPaymentDue(id: string): Promise<Payment> {
    if (!id?.trim()) {
      throw new Error("Payment ID is required");
    }
    try {
      return await this.financeRepository.markPaymentDue(id);
    } catch (error) {
      handleError(error, "Failed to mark payment as due");
    }
  }
  async submitInvoice(
    id: string,
    invoice: SubmitInvoiceRequest,
  ): Promise<Payment> {
    if (!id?.trim()) {
      throw new Error("Payment ID is required");
    }
    if (!invoice.invoiceNumber?.trim()) {
      throw new Error("Invoice number is required");
    }
    if (!invoice.invoiceDate) {
      throw new Error("Invoice date is required");
    }
    if (!invoice.invoiceAmount || invoice.invoiceAmount <= 0) {
      throw new Error("Invoice amount must be greater than 0");
    }
    if (!invoice.invoiceDocumentKey?.trim()) {
      throw new Error("Invoice document is required");
    }
    try {
      return await this.financeRepository.submitInvoice(id, invoice);
    } catch (error) {
      handleError(error, "Failed to submit invoice");
    }
  }
  async updateInvoice(
    id: string,
    invoice: UpdateInvoiceRequest,
  ): Promise<Payment> {
    if (!id?.trim()) {
      throw new Error("Payment ID is required");
    }
    try {
      return await this.financeRepository.updateInvoice(id, invoice);
    } catch (error) {
      handleError(error, "Failed to update invoice");
    }
  }
  async confirmPayment(
    id: string,
    data: ConfirmPaymentRequest,
  ): Promise<Payment> {
    if (!id?.trim()) {
      throw new Error("Payment ID is required");
    }
    if (!data.paymentDate) {
      throw new Error("Payment date is required");
    }
    if (!data.bankReference?.trim()) {
      throw new Error("Bank reference is required");
    }
    try {
      return await this.financeRepository.confirmPayment(id, data);
    } catch (error) {
      handleError(error, "Failed to confirm payment");
    }
  }
  async lockPayment(id: string): Promise<Payment> {
    if (!id?.trim()) {
      throw new Error("Payment ID is required");
    }
    try {
      return await this.financeRepository.lockPayment(id);
    } catch (error) {
      handleError(error, "Failed to lock payment");
    }
  }
  async deletePayment(id: string): Promise<void> {
    if (!id?.trim()) {
      throw new Error("Payment ID is required");
    }
    try {
      await this.financeRepository.deletePayment(id);
    } catch (error) {
      handleError(error, "Failed to delete payment");
    }
  }
  async uploadInvoiceDocument(
    id: string,
    file: File,
  ): Promise<UploadInvoiceDocumentResponse> {
    if (!id?.trim()) {
      throw new Error("Payment ID is required");
    }
    assertUploadWithin10Mb(file);
    try {
      return await this.financeRepository.uploadInvoiceDocument(id, file);
    } catch (error) {
      handleError(error, "Failed to upload invoice document");
    }
  }
  async getInvoiceDownloadUrl(
    id: string,
  ): Promise<DocumentDownloadUrlResponse> {
    if (!id?.trim()) {
      throw new Error("Payment ID is required");
    }
    try {
      return await this.financeRepository.getInvoiceDownloadUrl(id);
    } catch (error) {
      handleError(error, "Failed to get invoice download URL");
    }
  }
  async uploadPaymentProofDocument(
    id: string,
    file: File,
  ): Promise<UploadPaymentProofResponse> {
    if (!id?.trim()) {
      throw new Error("Payment ID is required");
    }
    assertUploadWithin10Mb(file);
    try {
      return await this.financeRepository.uploadPaymentProofDocument(id, file);
    } catch (error) {
      handleError(error, "Failed to upload payment proof document");
    }
  }
  async getPaymentProofDownloadUrl(
    id: string,
  ): Promise<DocumentDownloadUrlResponse> {
    if (!id?.trim()) {
      throw new Error("Payment ID is required");
    }
    try {
      return await this.financeRepository.getPaymentProofDownloadUrl(id);
    } catch (error) {
      handleError(error, "Failed to get payment proof download URL");
    }
  }
  // ---------------------- Dashboard ----------------------
  async getFinancialDashboard(
    studyId: string,
  ): Promise<FinancialDashboardData> {
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    try {
      return await this.financeRepository.getFinancialDashboard(studyId);
    } catch (error) {
      handleError(error, "Failed to fetch financial dashboard");
    }
  }
  // ---------------------- Audit ----------------------
  async getFinanceAuditLogs(
    filters?: AuditLogFilters,
    page = 1,
    limit = 10,
  ): Promise<FinanceListResponse<FinanceAuditLog>> {
    try {
      return await this.financeRepository.getFinanceAuditLogs(
        filters,
        page,
        limit,
      );
    } catch (error) {
      handleError(error, "Failed to fetch audit logs");
    }
  }
  async exportFinanceAuditLogs(
    studyId: string,
    format?: string,
  ): Promise<Blob> {
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    try {
      return await this.financeRepository.exportFinanceAuditLogs(
        studyId,
        format,
      );
    } catch (error) {
      handleError(error, "Failed to export audit logs");
    }
  }
}
